import { type ClassValue, clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * The PMS typographic roles declared under `@theme` in `styles.css` (`--text-display`,
 * `--text-h1` ... `--text-micro`). Tailwind v4 turns them into `text-display`, `text-h1`, etc.
 *
 * `tailwind-merge` only knows Tailwind's built-in sizes, so without this list it files these
 * classes under text COLOUR and `cn('text-caption', 'text-gray-700')` silently drops the size:
 * the element renders at the inherited 16px and nothing on screen or in a test says so.
 * `tests/cn-pms-font-sizes.test.ts` derives the expected list from the `--text-*` role tokens of
 * `styles.css`, so a role added there without its entry here fails a test instead of a screen.
 */
export const PMS_FONT_SIZES = [
  'display',
  'h1',
  'h2',
  'h3',
  'h4',
  'body-lg',
  'caption',
  'micro',
] as const;

const twMerge = extendTailwindMerge({
  extend: { classGroups: { 'font-size': [{ text: [...PMS_FONT_SIZES] }] } },
});

/**
 * Merge Tailwind class names, deduplicating conflicts.
 * Standard shadcn/ui utility - use on every component that accepts a `className` prop.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}
